import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { COMPANY, NAV_LINKS } from "../mock";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const waLink = `https://wa.me/${COMPANY.whatsappRaw}?text=${encodeURIComponent(
    COMPANY.whatsappMsg
  )}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${
        scrolled
          ? "bg-white/90 shadow-[0_4px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl"
          : "bg-white"
      }`}
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-500 lg:px-8 ${
          scrolled ? "py-2" : "py-3"
        }`}
      >
        <Link to="/" className="flex items-center gap-2 shrink-0 transition-transform duration-300 hover:scale-[1.03]">
          <img
            src={COMPANY.logo}
            alt="WBL Services"
            className={`w-auto transition-all duration-500 ${scrolled ? "h-16" : "h-20"}`}
          />
        </Link>

        <nav className="hidden items-center gap-9 xl:flex">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.path;
            return (
              <Link
                key={l.path}
                to={l.path}
                className={`nav-underline relative text-[15px] font-medium transition-colors duration-200 hover:text-sky-600 ${
                  active ? "active text-sky-600" : "text-slate-700"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <div className="flex flex-col items-end gap-0.5 border-r border-slate-200 pr-4">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="group flex items-center gap-2 text-sm font-semibold text-slate-700 transition-colors hover:text-sky-600"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-sky-50 text-sky-600 transition-transform duration-300 group-hover:scale-110">
                <Phone size={14} />
              </span>
              {COMPANY.phone}
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 text-sm font-semibold text-slate-700 transition-colors hover:text-green-600"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-50 text-green-600 transition-transform duration-300 group-hover:scale-110">
                <MessageCircle size={14} />
              </span>
              {COMPANY.whatsapp}
            </a>
          </div>
          <Link to="/contact">
            <Button className="btn-shine rounded-full bg-sky-500 px-6 font-semibold text-white shadow-md shadow-sky-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-sky-600 hover:shadow-lg hover:shadow-sky-500/40">
              Demander un devis
            </Button>
          </Link>
        </div>

        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 xl:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div
        className={`overflow-hidden border-t border-slate-100 bg-white transition-all duration-300 xl:hidden ${
          open ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="flex flex-col px-5 py-4">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className={`border-b border-slate-50 py-3 text-base font-medium transition-colors ${
                location.pathname === l.path ? "text-sky-600" : "text-slate-700"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            className="flex items-center gap-2 py-3 text-base font-semibold text-slate-700"
          >
            <Phone size={16} className="text-sky-600" /> {COMPANY.phone}
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-3 text-base font-semibold text-slate-700"
          >
            <MessageCircle size={16} className="text-green-600" /> {COMPANY.whatsapp}
          </a>
          <Link to="/contact" className="mt-2">
            <Button className="w-full rounded-full bg-sky-500 font-semibold text-white hover:bg-sky-600">
              Demander un devis
            </Button>
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
